import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Facility } from '../api_types';
import { HomeIcon } from '@heroicons/react/24/outline';

const FacilitySelection: React.FC = () => {
  const navigate = useNavigate();
  const { businessFacilities, user, logout } = useAuth();

  // Sort facilities using natural sort
  const sortedFacilities = useMemo(() => {
    return [...businessFacilities].sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      
      // Split the names into parts (text and numbers)
      const aParts = aName.match(/(\d+|\D+)/g) || [];
      const bParts = bName.match(/(\d+|\D+)/g) || [];
      
      // Compare each part
      for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
        const aPart = aParts[i];
        const bPart = bParts[i];
        
        // If both parts are numbers, compare numerically
        if (/^\d+$/.test(aPart) && /^\d+$/.test(bPart)) {
          const diff = parseInt(aPart) - parseInt(bPart);
          if (diff !== 0) return diff;
        } else {
          // Otherwise compare as strings
          const diff = aPart.localeCompare(bPart);
          if (diff !== 0) return diff;
        }
      }
      
      // If all parts match up to the length of the shorter name,
      // the shorter name comes first
      return aParts.length - bParts.length;
    });
  }, [businessFacilities]);

  const handleFacilitySelect = (facility: Facility) => {
    // Store both facility and business IDs in localStorage
    localStorage.setItem('selectedFacilityId', facility.id);
    localStorage.setItem('selectedBusinessId', facility.business_id);
    // Navigate to the dashboard
    navigate('/business/dashboard');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/business/login', { replace: true });
  };

  if (!user || user.role !== 'business') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Unauthorized access</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div className="text-center w-full">
            <h2 className="text-3xl font-bold text-gray-900 flex items-center justify-center gap-2">
              <HomeIcon className="h-8 w-8 text-indigo-500" />
              Select a Facility
            </h2>
            <p className="mt-2 text-lg text-gray-600">
              Choose which facility you want to manage
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="ml-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium shadow transition-all"
          >
            Logout
          </button>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {sortedFacilities.map((facility) => (
            <button
              key={facility.id}
              onClick={() => handleFacilitySelect(facility)}
              className="relative bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden border border-gray-200"
            >
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900">{facility.name}</h3>
                {facility.address && (
                  <p className="mt-2 text-sm text-gray-500">{facility.address}</p>
                )}
                {facility.phone_number && (
                  <p className="mt-1 text-sm text-gray-500">{facility.phone_number}</p>
                )}
              </div>
              <div className="bg-gray-50 px-6 py-3">
                <div className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                  Select Facility →
                </div>
              </div>
            </button>
          ))}
        </div>

        {businessFacilities.length === 0 && (
          <div className="text-center mt-12">
            <p className="text-gray-500">No facilities available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FacilitySelection; 